import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import PropTypes from "prop-types";
import CompanySelect from "src/modules/consumer-engagement/components/select/companySelect";

const useStyle = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  companyField: {
    position: "relative",
  },
  companyLabel: {
    position: "absolute",
    top: -8,
    left: 10,
    zIndex: 1,
    padding: "0 4px",
    lineHeight: 1,
    backgroundColor: theme.palette.background.paper,
  },
  resultCard: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

const ManagementSnPage = ({
  branches,
  selectedCompany,
  selectedBranch,
  inputType,
  validateResult,
  isValidating,
  isMoving,
  onCompanyChange,
  onBranchChange,
  onInputTypeChange,
  onValidate,
  onMove,
}) => {
  const classes = useStyle();
  const [fromSn, setFromSn] = useState("");
  const [toSn, setToSn] = useState("");

  const isSeqMode = inputType === "sequence_number";
  const fromLabel = isSeqMode ? "From Sequence Number" : "From Serial Number";
  const toLabel = isSeqMode ? "To Sequence Number" : "To Serial Number";

  const handleValidateClick = () => {
    if (fromSn && toSn) {
      onValidate(fromSn, toSn);
    }
  };

  const handleMoveClick = () => {
    if (fromSn && toSn) {
      onMove(fromSn, toSn);
    }
  };

  const isFormValid = fromSn && toSn && selectedCompany && selectedBranch;
  const canMove =
    isFormValid && validateResult && validateResult.success && !isMoving;

  return (
    <>
      <Helmet>
        <title>Management SN | Kood Asia Management Portal</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 3,
        }}
      >
        <Container maxWidth={false}>
          <Card className={classes.card}>
            <Typography variant="h4" sx={{ padding: 2 }}>
              Management Serial Numbers
            </Typography>
          </Card>

          <Card>
            <CardContent sx={{ paddingTop: 3 }}>
              <Typography variant="h6" gutterBottom>
                Move Pre-Print SNs to Company
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box className={classes.companyField}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className={classes.companyLabel}
                    >
                      Company *
                    </Typography>
                    <CompanySelect
                      placeholder="-- Select Company --"
                      value={selectedCompany}
                      handleChange={onCompanyChange}
                      style={{
                        border: "1px solid #c4c4c4",
                        backgroundColor: "white",
                        height: 56,
                        padding: "8px 14px",
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Branch / Node"
                    variant="outlined"
                    fullWidth
                    required
                    value={selectedBranch ? selectedBranch.id : ""}
                    onChange={(e) => {
                      const branch = branches.find(
                        (b) => b.id === Number(e.target.value)
                      );
                      onBranchChange(branch || null);
                    }}
                    disabled={!selectedCompany}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                  >
                    <option value="">-- Select --</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} (ID: {branch.id})
                      </option>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={inputType}
                      onChange={(e) => {
                        onInputTypeChange(e.target.value);
                        setFromSn("");
                        setToSn("");
                      }}
                    >
                      <FormControlLabel
                        value="serial_number"
                        control={<Radio color="primary" />}
                        label="Serial Number"
                      />
                      <FormControlLabel
                        value="sequence_number"
                        control={<Radio color="primary" />}
                        label="Sequence Number"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={fromLabel}
                    variant="outlined"
                    fullWidth
                    required
                    value={fromSn}
                    onChange={(e) => setFromSn(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={toLabel}
                    variant="outlined"
                    fullWidth
                    required
                    value={toSn}
                    onChange={(e) => setToSn(e.target.value)}
                  />
                </Grid>
              </Grid>

              <Box className={classes.buttonGroup}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleValidateClick}
                  disabled={!fromSn || !toSn || isValidating}
                  startIcon={isValidating && <CircularProgress size={20} />}
                >
                  {isValidating ? "Validating..." : "Validate Range"}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleMoveClick}
                  disabled={!canMove}
                  startIcon={isMoving && <CircularProgress size={20} />}
                >
                  {isMoving ? "Moving..." : "Move to Company"}
                </Button>
              </Box>

              {validateResult && (
                <Card className={classes.resultCard} variant="outlined">
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      color={
                        validateResult.success ? "textPrimary" : "error"
                      }
                    >
                      {validateResult.success
                        ? "Validation Result"
                        : "Validation Failed"}
                    </Typography>

                    {validateResult.success ? (
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            Available SNs
                          </Typography>
                          <Typography variant="h6">
                            {validateResult.available_count}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            Batch Number
                          </Typography>
                          <Typography variant="body1">
                            {validateResult.job?.batch_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            Format
                          </Typography>
                          <Typography variant="body1">
                            {validateResult.job?.format}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            Generation Type
                          </Typography>
                          <Typography variant="body1">
                            {validateResult.job?.gen_type}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            URL
                          </Typography>
                          <Typography variant="body1">
                            {validateResult.job?.url}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            Seq Range
                          </Typography>
                          <Typography variant="body1">
                            {validateResult.from_seq_num} -{" "}
                            {validateResult.to_seq_num}
                          </Typography>
                        </Grid>
                      </Grid>
                    ) : (
                      <Typography variant="body1" color="error">
                        {validateResult.message}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

ManagementSnPage.propTypes = {
  branches: PropTypes.array.isRequired,
  selectedCompany: PropTypes.object,
  selectedBranch: PropTypes.object,
  inputType: PropTypes.string.isRequired,
  validateResult: PropTypes.object,
  isValidating: PropTypes.bool.isRequired,
  isMoving: PropTypes.bool.isRequired,
  onCompanyChange: PropTypes.func.isRequired,
  onBranchChange: PropTypes.func.isRequired,
  onInputTypeChange: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
};

export default ManagementSnPage;
